import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL as BASE_URL, SITE_NAME } from "@/lib/constants";
import indexedSpokes from "@/data/indexedSpokes.json";
import indexedComparePairs from "@/data/indexedComparePairs.json";
import { COMPARE_PAIR_SLUGS } from "@/data/comparePairs";

const DEFAULT_OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/sFuCcl2LlYZsA8NOJqxthke1YXm1/social-images/social-1773222851431-A0507011-9498-41E1-9F90-74EB6ACEADAF.webp";

/**
 * Normalize a path for use as the canonical URL:
 *  - lowercase the path segment (but not query — we're stripping that anyway)
 *  - strip trailing slashes except for root
 *  - strip all query parameters and fragments (UTM, fbclid, gclid, ref, etc. never
 *    belong in a canonical — they cause Google Search Console "duplicate without
 *    user-selected canonical" warnings)
 *
 * This runs ONLY on the runtime router pathname (the no-canonical-prop branch).
 * It must NEVER be applied to a caller-supplied absolute canonical: collapsing
 * "//" would turn "https://" into "https:/", the host parses as "https", and
 * you get exactly the malformed-canonical symptom seen in old prerender
 * snapshots (audit 2026-09-03).
 */
function normalizeCanonicalPath(rawPath: string): string {
  // Strip query and hash
  const pathOnly = rawPath.split("?")[0].split("#")[0];
  // Normalize: lowercase, collapse duplicate slashes, trim trailing slash (except /)
  const lower = pathOnly.toLowerCase();
  const collapsed = lower.replace(/\/{2,}/g, "/");
  if (collapsed === "/") return "/";
  return collapsed.replace(/\/+$/, "");
}

/**
 * Return a category-aware OG image based on URL path prefix.
 * TODO: Upload category-specific images to the CDN and uncomment the mappings below.
 * Until then, all pages use the same verified DEFAULT_OG_IMAGE to avoid broken social previews.
 */
function getCategoryOGImage(_path: string): string {
  // When category images are uploaded, uncomment:
  // const CDN = "https://storage.googleapis.com/gpt-engineer-file-uploads/sFuCcl2LlYZsA8NOJqxthke1YXm1/social-images";
  // if (path.startsWith("/compound/")) return `${CDN}/og-peptides.webp`;
  // if (path.startsWith("/nootropic/")) return `${CDN}/og-nootropics.webp`;
  // if (path.startsWith("/tools/")) return `${CDN}/og-tools.webp`;
  // if (path.startsWith("/blog/")) return `${CDN}/og-blog.webp`;
  // if (path.startsWith("/brain-health")) return `${CDN}/og-brain-health.webp`;
  // if (path.startsWith("/guides/")) return `${CDN}/og-guides.webp`;
  return DEFAULT_OG_IMAGE;
}

/**
 * A word-boundary trim can leave the separator that sat before the brand
 * suffix hanging at the end ("... Pricing |", "... Rankings —"). Strip any
 * trailing separator run so a trimmed title or description still reads as a
 * complete phrase. Audit 2026-09-04: /tools/cost-calculator and
 * /nootropics/compare shipped with exactly that dangling "|" / "—" in their
 * prerendered <title>.
 */
function stripDanglingSeparator(text: string): string {
  return text.replace(/[\s|—–\-:;,·&]+$/u, "");
}

/** Commerce routes kept out of search results (noindex, follow) and out of the sitemap. */
export function isCommercePath(path: string): boolean {
  const p = path.toLowerCase();
  return p.startsWith("/buy/") || p.startsWith("/coupons/") || p.startsWith("/compare/vendors/")
    || p === "/vendors/bhg-labs/products" || p.startsWith("/vendors/bhg-labs/products/");
}

/**
 * Per-compound pages folded into the compound's main guide (owner decision 2026-10-24): side effects, alternatives,
 * protocol and dosage copies. They stay live for visitors and link to the main guide at the top, but they are kept
 * out of search results (noindex, follow, self canonical) and out of the regular sitemaps, so each compound has one
 * page in Google instead of five near-identical ones. The exception is src/data/indexedSpokes.json: the side pages
 * search engines actually send visitors to (5 or more entries in six months) stay indexed. Keep identical to
 * isMergedSpokePath in scripts/generate-prerender-stubs.mjs and isMergedSpokeLoc in scripts/regenerate-sitemap.mjs.
 */
const INDEXED_SPOKES = new Set(indexedSpokes.pages.map((page) => page.path));
export function isMergedSpokePath(path: string): boolean {
  const p = path.toLowerCase().replace(/\/+$/, "");
  return (p.startsWith("/side-effects/") || p.startsWith("/alternatives-to/") || p.startsWith("/protocol/")
    || p.startsWith("/guides/dosage/")) && !INDEXED_SPOKES.has(p);
}

/**
 * Compound comparison pages, /compare/<a>-vs-<b> (2026-10-25). Any two compounds render a comparison, and the "Compare
 * vs" buttons on the alternatives pages linked pairs nobody searches for, in both orders. Two kinds of pair stay
 * indexed: the hand-picked list in src/data/comparePairs.ts, and the pairs search engines send visitors to
 * (src/data/indexedComparePairs.json, 5 or more entries in six months). The reverse order of a kept pair is the same
 * page, so it carries a canonical to the kept order (keptComparePairPath). Every other pair page gets noindex, follow
 * with a self canonical. Keep identical to isUnlistedComparePairPath in scripts/generate-prerender-stubs.mjs and
 * isUnlistedCompareLoc in scripts/regenerate-sitemap.mjs.
 */
const KEPT_COMPARE_PAIRS = new Set([...COMPARE_PAIR_SLUGS, ...indexedComparePairs.pairs.map((p) => p.pair)]);
function comparePairOf(path: string): string | null {
  const m = path.toLowerCase().replace(/\/+$/, "").match(/^\/compare\/([a-z0-9-]+-vs-[a-z0-9-]+)$/);
  return m ? m[1] : null;
}
/** The indexed address of a comparison page: itself when kept, the kept order when its reverse is kept, else null. */
export function keptComparePairPath(path: string): string | null {
  const pair = comparePairOf(path);
  if (!pair) return null;
  if (KEPT_COMPARE_PAIRS.has(pair)) return `/compare/${pair}`;
  const i = pair.indexOf("-vs-");
  const reverse = `${pair.slice(i + 4)}-vs-${pair.slice(0, i)}`;
  return KEPT_COMPARE_PAIRS.has(reverse) ? `/compare/${reverse}` : null;
}
export function isUnlistedComparePairPath(path: string): boolean {
  return comparePairOf(path) !== null && keptComparePairPath(path) === null;
}

interface SEOHeadProps {
  title: string;
  preserveTitle?: boolean;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
  ogType?: string;
  dateModified?: string;
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
}

export default function SEOHead({
  title,
  preserveTitle = false,
  description,
  canonical,
  ogImage,
  noIndex = false,
  schemaJson,
  ogType = "website",
  dateModified,
  articleMeta,
}: SEOHeadProps) {
  // Build the full title without mutating it with a hardcoded "..." — Google
  // and other engines render their own ellipsis in SERPs based on pixel width,
  // not char count. Pages should write titles ≤60 chars themselves; if an
  // author overshoots, we trim to the last whole word so the meta tag always
  // reads as a clean, complete phrase (never "foo bar baz...").
  let fullTitle = title.includes(SITE_NAME)
    ? title
    : title.length > 42
      ? title
      : `${title} | ${SITE_NAME}`;
  if (!preserveTitle && fullTitle.length > 60) {
    const cutAt = fullTitle.lastIndexOf(" ", 60);
    fullTitle = stripDanglingSeparator(cutAt > 20 ? fullTitle.slice(0, cutAt) : fullTitle.slice(0, 60));
  }
  // Same treatment for description: trim on word boundary, no dangling dots.
  let desc = description;
  if (desc.length > 160) {
    const cutAt = desc.lastIndexOf(" ", 160);
    desc = stripDanglingSeparator(cutAt > 120 ? desc.slice(0, cutAt) : desc.slice(0, 160));
  }
  // Floor as well as ceiling: a description under ~50 chars (a bare blog
  // summary or vendor tagline fallback) reads as thin in SERPs and trips
  // "meta description too short" audits. Pad with a stable brand clause,
  // then re-apply the 160 ceiling.
  if (desc.trim().length < 50) {
    const base = desc.trim().replace(/[.\s]+$/, "");
    desc = `${base ? `${base}. ` : ""}Research compound guides, vendor prices and dosing tools from ${SITE_NAME}.`;
    if (desc.length > 160) desc = stripDanglingSeparator(desc.slice(0, desc.lastIndexOf(" ", 160)));
  }
  // Prefer React Router's location so the pathname is correct even during the
  // first render of a lazily-loaded route (window.location is also correct, but
  // useLocation() updates synchronously with the router and avoids any stale
  // read during fast route transitions).
  const location = useLocation();
  const currentPath = location?.pathname || (typeof window !== "undefined" ? window.location.pathname : "/");
  const image = ogImage || getCategoryOGImage(currentPath);

  // Build the canonical. If the caller supplied one, honor the host they chose
  // but still strip query/hash. If not, construct from BASE_URL + normalized path.
  let cleanCanonical: string;
  if (canonical) {
    // Accept caller-provided full URL; strip query/hash, force https + www host.
    const withoutFragment = canonical.split("?")[0].split("#")[0];
    cleanCanonical = withoutFragment
      .replace(/^http:\/\//, "https://")
      .replace("://bodyhackguide.co", "://www.bodyhackguide.co");
    // If caller passed only a path (e.g. "/about"), bolt on the base URL.
    if (cleanCanonical.startsWith("/")) cleanCanonical = `${BASE_URL}${cleanCanonical}`;
  } else {
    cleanCanonical = `${BASE_URL}${normalizeCanonicalPath(currentPath)}`;
  }

  // Stable serialization of schemaJson for the effect dependency — keeps the
  // effect idempotent across renders when the same schema is passed.
  //
  // Also deduplicates by @type: Google Search Console reports "Duplicate field
  // FAQPage" (or similar) when two JSON-LD blocks share the same top-level
  // @type. Historically this has happened on BHG during render transitions
  // when async data (listings, FAQs) arrived in multiple waves, briefly
  // double-mounting schema with the same @type before the effect cleanup
  // caught up. Deduplicating at serialization time guarantees a single
  // script tag per @type regardless of how many array entries share it.
  const schemaJsonStrings = useMemo(() => {
    if (!schemaJson) return [] as string[];
    const items = Array.isArray(schemaJson) ? schemaJson : [schemaJson];
    const seenTypes = new Set<string>();
    const deduped: Record<string, unknown>[] = [];
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const type = (item as { "@type"?: unknown })["@type"];
      const typeKey = Array.isArray(type) ? type.join("|") : String(type || "");
      if (typeKey && seenTypes.has(typeKey)) continue;
      if (typeKey) seenTypes.add(typeKey);
      deduped.push(item);
    }
    return deduped.map(item => JSON.stringify(item));
  }, [schemaJson]);

  // Direct DOM injection for JSON-LD. react-helmet-async does not reliably
  // render <script type="application/ld+json"> into the live DOM on client
  // navigations — only <title>/<meta>/<link> flush. This effect appends the
  // schema scripts with a page-scoped attribute so they can be torn down on
  // route change or prop update without clobbering PageBreadcrumbs' own
  // injected BreadcrumbList (which uses data-bhg-breadcrumb-jsonld).
  //
  // Cleanup is done synchronously on every run: we sweep ALL tagged scripts
  // first (not just nodes we previously created) so any stragglers left behind
  // by a sibling SEOHead unmount, a React StrictMode double-invoke, or a fast
  // route change are guaranteed gone before we re-inject. The returned
  // cleanup then removes only our own nodes on unmount.
  useEffect(() => {
    // The inline JSX <script> tags below are the single source of JSON-LD: they
    // are in the static render (so the prerender snapshot carries them) and they
    // re-render on every SPA navigation. Earlier builds ALSO appended head copies
    // here and tried to sweep the inline ones with a head-scoped query that never
    // matched (the inline tags live in <body>), so every schema rendered twice
    // (verified 2026-09-05: 8 ld+json tags for 4 schemas on every /stack page).
    // Now: no head injection. Sweep legacy runtime copies, and when this page
    // renders its own schema, drop stub-baked head copies too (the prerender
    // stub for some routes bakes JSON-LD into <head>; the breadcrumb component
    // manages its own tagged node and is left alone).
    document.head.querySelectorAll("script[data-bhg-seo-jsonld]").forEach(n => n.remove());
    if (schemaJsonStrings.length > 0) {
      document.head
        .querySelectorAll('script[type="application/ld+json"]:not([data-bhg-breadcrumb-jsonld])')
        .forEach(n => n.remove());
    }
  }, [schemaJsonStrings]);

  return (
    <>
      <Helmet defer={false}>
        <title>{fullTitle}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={cleanCanonical} />

        {/* Robots. Commerce pages (/buy/, /coupons/, /compare/vendors/), the per-compound pages merged into the
            main guide (isMergedSpokePath) and the comparison pairs outside the kept list (isUnlistedComparePairPath)
            stay live but out of the index. */}
        {isCommercePath(currentPath) || isMergedSpokePath(currentPath) || isUnlistedComparePairPath(currentPath) ? (
          <meta name="robots" content="noindex, follow" />
        ) : noIndex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}

        {/* Open Graph */}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={cleanCanonical} />
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:image" content={image} />

        {/* Date modified — freshness signal for AI crawlers */}
        {dateModified && <meta name="date" content={dateModified} />}
        {dateModified && <meta property="article:modified_time" content={dateModified} />}

        {/* Article-specific OG tags */}
        {articleMeta?.publishedTime && <meta property="article:published_time" content={articleMeta.publishedTime} />}
        {articleMeta?.modifiedTime && !dateModified && <meta property="article:modified_time" content={articleMeta.modifiedTime} />}
        {articleMeta?.author && <meta property="article:author" content={articleMeta.author} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={image} />
      </Helmet>

      {/*
        JSON-LD schemas rendered as INLINE JSX siblings to Helmet — not
        inside Helmet, not inside useEffect. Why:

        - Inside Helmet: react-helmet-async serializes its children but its
          SSR head-emit code path strips <script> children in some setups
          (including the Lovable / lovablehtml prerender pipeline). Verified
          2026-06-02 that placing the schemas inside <Helmet> produced zero
          ld+json tags in the prerendered HTML.
        - useEffect only: runs on the client after hydration → the
          prerendered snapshot that Googlebot/lovablehtml cache contains
          none of the schemas.

        Inline in the JSX tree: React's renderToString() includes them in
        the static HTML output, the prerender pipeline captures them, and
        Google parses JSON-LD from anywhere in the document (head or body).

        The useEffect above still runs to keep schemas in sync on SPA
        navigations (when the inline copy doesn't re-render fast enough or
        when prop changes need to flush immediately). The inline scripts
        are tagged data-bhg-seo-jsonld-ssr so the effect can sweep its
        duplicates after hydration.
      */}
      {schemaJsonStrings.map((json, i) => (
        <script
          key={`bhg-jsonld-${i}`}
          type="application/ld+json"
          data-bhg-seo-jsonld-ssr=""
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: json }}
        />
      ))}
    </>
  );
}
